// @ts-check
/** @typedef {import('../types/api').SeoHealth} SeoHealth */
/** @typedef {import('../types/api').SeoProduct} SeoProduct */
/** @typedef {import('../types/api').SeoCollection} SeoCollection */

import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Globe,
  Image,
  RefreshCw,
  Search,
  Sparkles,
  Type,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import SiteSeoTab from '../components/seo/SiteSeoTab';
import { useToast } from '../context/ToastContext';
import adminSeoService from '../services/adminSeoService';

/* ─── Score ring ─── */
const ScoreRing = ({ score, size = 120 }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <span className="absolute text-2xl font-bold" style={{ color }}>
        {score}
      </span>
    </div>
  );
};

/* ─── Stat card ─── */
const StatCard = ({ icon: Icon, label, value, total, color = 'blue' }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const colors = {
    green: 'bg-green-50 dark:bg-green-950/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900',
    yellow:
      'bg-yellow-50 dark:bg-yellow-950/50 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-900',
    red: 'bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900',
    blue: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900',
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">
        {value}/{total}
      </div>
      <div className="text-xs mt-1 opacity-75">{pct}% complete</div>
    </div>
  );
};

/* ─── Inline SEO editor ─── */
const SeoEditor = ({ item, type, onSave }) => {
  const [open, setOpen] = useState(false);
  const [seoTitle, setSeoTitle] = useState(item.seo_title || '');
  const [seoDescription, setSeoDescription] = useState(item.seo_description || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const toast = useToast();

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await adminSeoService.generate({
        name: item.name,
        description: item.description,
        short_description: item.short_description,
      });
      setSeoTitle((result.seo_title || '').slice(0, 70));
      setSeoDescription((result.seo_description || '').slice(0, 160));
    } catch {
      toast.error('Failed to generate SEO copy');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (type === 'product') {
        await adminSeoService.updateProductSeo(item.id, {
          seo_title: seoTitle || null,
          seo_description: seoDescription || null,
        });
      } else {
        await adminSeoService.updateCollectionSeo(item.id, {
          seo_title: seoTitle || null,
          seo_description: seoDescription || null,
        });
      }
      toast.success(`SEO updated for ${item.name}`);
      if (onSave) onSave({ ...item, seo_title: seoTitle || null, seo_description: seoDescription || null });
      setOpen(false);
    } catch {
      toast.error('Failed to save SEO data');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-lg bg-card">
      {/* Header row */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-muted/50 transition-colors cursor-pointer"
      >
        {item.image_urls?.[0] || item.hero_image ? (
          <img
            src={item.image_urls?.[0] || item.hero_image}
            alt={item.name}
            className="w-12 h-12 object-cover rounded-lg"
          />
        ) : (
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
            <Image className="w-5 h-5 text-muted-foreground/70" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{item.name}</div>
          <div className="text-xs text-muted-foreground truncate">
            /{type === 'product' ? 'product' : 'collection'}/{item.slug}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {item.seo_complete ? (
            <span className="inline-flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
              <CheckCircle className="w-3 h-3" /> Complete
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Incomplete
            </span>
          )}
          {open ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground/70" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground/70" />
          )}
        </div>
      </button>

      {/* Expanded editor */}
      {open && (
        <div className="px-4 pb-4 border-t border-border/60">
          <div className="mt-4 space-y-4">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted/50 disabled:opacity-50"
              title="Draft the SEO title & description from the item's name and description"
            >
              <Sparkles className={`w-3 h-3 ${generating ? 'animate-pulse' : ''}`} />
              {generating ? 'Generating…' : 'Generate with AI'}
            </button>

            {/* SEO Title */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                <label className="text-sm font-medium text-foreground/80">SEO Title</label>
                <span className={`text-xs ${seoTitle.length > 60 ? 'text-red-500' : 'text-muted-foreground/70'}`}>
                  {seoTitle.length}/70
                </span>
              </div>
              <input
                type="text"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value.slice(0, 70))}
                placeholder={`${item.name} — KAAY`}
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-foreground focus:border-foreground"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Leave empty to auto-generate from product name. Appears in search results and browser tab.
              </p>
            </div>

            {/* SEO Description */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                <label className="text-sm font-medium text-foreground/80">SEO Description</label>
                <span
                  className={`text-xs ${seoDescription.length > 155 ? 'text-red-500' : 'text-muted-foreground/70'}`}
                >
                  {seoDescription.length}/160
                </span>
              </div>
              <textarea
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value.slice(0, 160))}
                placeholder="Brief description for search engines..."
                rows={3}
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-foreground focus:border-foreground resize-none"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Leave empty to use short description. This appears below the title in search results.
              </p>
            </div>

            {/* Google preview */}
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Search Preview</p>
              <div className="text-[#1a0dab] text-base leading-tight truncate">{seoTitle || `${item.name} — KAAY`}</div>
              <div className="text-[#006621] text-xs mt-0.5 truncate">
                kaay.co/{type === 'product' ? 'product' : 'collection'}/{item.slug}
              </div>
              <div className="text-sm text-[#545454] mt-1 line-clamp-2">
                {seoDescription || item.short_description || item.description || 'No description set'}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Save
              </button>
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <a
                href={`https://kaay.co/${type === 'product' ? 'product' : 'collection'}/${item.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              >
                View live <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Page ─── */
const SeoPage = () => {
  const toast = useToast();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Health data
  /** @type {[SeoHealth | null, Function]} */
  const [health, setHealth] = useState(null);

  // Product SEO data
  /** @type {[SeoProduct[], Function]} */
  const [products, setProducts] = useState([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productFilter, setProductFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);

  // Collection SEO data
  /** @type {[SeoCollection[], Function]} */
  const [collections, setCollections] = useState([]);

  const loadHealth = useCallback(async () => {
    try {
      const data = await adminSeoService.getHealth();
      setHealth(data);
    } catch {
      toast.error('Failed to load SEO health data');
    }
  }, [toast]);

  const loadProducts = useCallback(
    async (page = 1) => {
      setProductsLoading(true);
      try {
        const data = await adminSeoService.getProducts({
          page,
          limit: 20,
          filter: productFilter || undefined,
          search: productSearch || undefined,
        });
        setProducts(data.items || []);
        setProductTotal(data.total || 0);
        setProductPage(page);
      } catch {
        toast.error('Failed to load product SEO data');
      } finally {
        setProductsLoading(false);
      }
    },
    [productFilter, productSearch, toast],
  );

  const loadCollections = useCallback(async () => {
    try {
      const data = await adminSeoService.getCollections();
      setCollections(data || []);
    } catch {
      toast.error('Failed to load collection SEO data');
    }
  }, [toast]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadHealth(), loadProducts(), loadCollections()]);
      setLoading(false);
    };
    load();
  }, [loadHealth, loadProducts, loadCollections]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadHealth(), loadProducts(productPage), loadCollections()]);
    setRefreshing(false);
  };

  // Reload products when filter/search changes
  useEffect(() => {
    if (!loading) loadProducts(1);
  }, [productFilter, productSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingScreen />;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Globe },
    { id: 'site', label: 'Site & Pages', icon: Sparkles },
    { id: 'products', label: 'Products', icon: FileText },
    { id: 'collections', label: 'Collections', icon: Type },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">SEO Manager</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor and improve your site&apos;s search visibility</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://kaay.co/sitemap.xml"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            Sitemap <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://kaay.co/robots.txt"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            robots.txt <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={handleRefresh}
            className="p-2 border border-input rounded-lg hover:bg-muted/50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 flex-shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
              tab === t.id
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && health && (
        <div>
          {/* Score + summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6 flex flex-col items-center justify-center">
              <ScoreRing score={health.overall_score} />
              <p className="text-sm font-medium mt-3">Overall SEO Score</p>
              <p className="text-xs text-muted-foreground mt-1">Based on meta data completeness</p>
            </div>

            {/* Product stats */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Products ({health.products.total})
              </h3>
              <div className="space-y-3">
                <StatCard
                  icon={Type}
                  label="SEO Title"
                  value={health.products.with_seo_title}
                  total={health.products.total}
                  color={health.products.with_seo_title === health.products.total ? 'green' : 'yellow'}
                />
                <StatCard
                  icon={FileText}
                  label="SEO Description"
                  value={health.products.with_seo_description}
                  total={health.products.total}
                  color={health.products.with_seo_description === health.products.total ? 'green' : 'yellow'}
                />
                <StatCard
                  icon={Image}
                  label="Primary Image"
                  value={health.products.with_image}
                  total={health.products.total}
                  color={health.products.with_image === health.products.total ? 'green' : 'red'}
                />
              </div>
            </div>

            {/* Collection stats */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Collections ({health.collections.total})
              </h3>
              <div className="space-y-3">
                <StatCard
                  icon={Type}
                  label="SEO Title"
                  value={health.collections.with_seo_title}
                  total={health.collections.total}
                  color={health.collections.with_seo_title === health.collections.total ? 'green' : 'yellow'}
                />
                <StatCard
                  icon={FileText}
                  label="SEO Description"
                  value={health.collections.with_seo_description}
                  total={health.collections.total}
                  color={health.collections.with_seo_description === health.collections.total ? 'green' : 'yellow'}
                />
                <StatCard
                  icon={Image}
                  label="Hero Image"
                  value={health.collections.with_image}
                  total={health.collections.total}
                  color={health.collections.with_image === health.collections.total ? 'green' : 'red'}
                />
              </div>
            </div>
          </div>

          {/* Checklist */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-semibold mb-4">SEO Checklist</h3>
            <div className="space-y-3">
              {[
                { done: true, text: 'Dynamic meta tags on all pages (react-helmet-async)' },
                { done: true, text: 'robots.txt configured with AI crawler rules' },
                { done: true, text: 'Dynamic sitemap.xml generated from database' },
                { done: true, text: 'JSON-LD structured data (Product, Organization, FAQ, WebSite)' },
                { done: true, text: 'Open Graph + Twitter Card tags on all pages' },
                { done: true, text: 'Product URLs use SEO-friendly slugs' },
                { done: true, text: 'llms.txt for AI platform discoverability' },
                { done: true, text: 'Visible breadcrumb navigation on key pages' },
                { done: true, text: 'Semantic HTML (main, article, section, aria-labels)' },
                {
                  done: health.products.with_seo_title === health.products.total,
                  text: `All products have custom SEO titles (${health.products.with_seo_title}/${health.products.total})`,
                },
                {
                  done: health.products.with_seo_description === health.products.total,
                  text: `All products have custom SEO descriptions (${health.products.with_seo_description}/${health.products.total})`,
                },
                {
                  done: health.collections.with_seo_title === health.collections.total,
                  text: `All collections have custom SEO titles (${health.collections.with_seo_title}/${health.collections.total})`,
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  {item.done ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  )}
                  <span className={`text-sm ${item.done ? 'text-foreground/80' : 'text-foreground font-medium'}`}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Products Tab */}
      {tab === 'products' && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
              <input
                type="text"
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-input rounded-lg text-sm"
              />
            </div>
            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="border border-input rounded-lg px-3 py-2 text-sm bg-card"
            >
              <option value="">All Products</option>
              <option value="missing_seo_title">Missing SEO Title</option>
              <option value="missing_seo_description">Missing SEO Description</option>
              <option value="missing_image">Missing Image</option>
              <option value="complete">SEO Complete</option>
            </select>
            <span className="text-sm text-muted-foreground self-center">{productTotal} products</span>
          </div>

          {/* Product list */}
          {productsLoading ? (
            <div className="py-12 text-center text-muted-foreground/70">Loading...</div>
          ) : (
            <div className="space-y-2">
              {products.map((product) => (
                <SeoEditor
                  key={product.id}
                  item={product}
                  type="product"
                  onSave={(updated) => {
                    setProducts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
                  }}
                />
              ))}
              {products.length === 0 && (
                <div className="py-12 text-center text-muted-foreground/70">No products match your filter</div>
              )}
            </div>
          )}

          {/* Pagination */}
          {productTotal > 20 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => loadProducts(productPage - 1)}
                disabled={productPage <= 1}
                className="px-3 py-1 border border-input rounded-lg text-sm disabled:opacity-30"
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {productPage} of {Math.ceil(productTotal / 20)}
              </span>
              <button
                onClick={() => loadProducts(productPage + 1)}
                disabled={productPage >= Math.ceil(productTotal / 20)}
                className="px-3 py-1 border border-input rounded-lg text-sm disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Site & Pages Tab */}
      {tab === 'site' && <SiteSeoTab />}

      {/* Collections Tab */}
      {tab === 'collections' && (
        <div className="space-y-2">
          {collections.map((collection) => (
            <SeoEditor
              key={collection.id}
              item={collection}
              type="collection"
              onSave={(updated) => {
                setCollections((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
              }}
            />
          ))}
          {collections.length === 0 && (
            <div className="py-12 text-center text-muted-foreground/70">No published collections found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeoPage;
